import React from 'react';
import { payoutIntegrationModel, invoiceIntegrationModel, integrationFailureModel } from "../models";
import { FailureCode } from "../models/integration-failure";
import ExternalLinkButton from "../components/ExternalLinkButton";
import { getHelpArticle } from "../infra/utils";
import ContactSupportButton from "../components/ContactSupportButton";
const reactivateItemsUrl = getHelpArticle('8385713-how-to-reactivate-items-in-quickbooks-online');
const unlinkPaymentUrl = getHelpArticle('8544439-how-to-unlink-a-payment-from-a-deposit-in-quickbooks-online');
const accountPeriodClosedUrl = getHelpArticle('8676191-how-to-resolve-a-quickbooks-online-sync-error-for-a-closed-account-period');
const getContactSupportButton = capitalize => /*#__PURE__*/React.createElement(ContactSupportButton, {
  testid: "contact-support",
  capitalize: capitalize
});
const getReadMoreButton = function getReadMoreButton() {
  let link = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : reactivateItemsUrl;
  return /*#__PURE__*/React.createElement(ExternalLinkButton, {
    openInNewTab: true,
    variant: "paragraph2",
    testid: "read-more",
    href: link,
    size: "medium"
  }, "Read more");
};
var FailureType = /*#__PURE__*/function (FailureType) {
  FailureType["Invoice"] = "Invoice";
  FailureType["Payment"] = "Payment";
  FailureType["Deposit"] = "Deposit";
  FailureType["Refund"] = "Refund";
  return FailureType;
}(FailureType || {});
export function getPayoutQboErrorMessage(payoutIntegration) {
  switch (payoutIntegration.status) {
    case payoutIntegrationModel.Status.SyncFailed:
      return getFailureMessage(payoutIntegration, FailureType.Deposit);
    default:
      return null;
  }
}
export function getInvoiceQboErrorMessage(invoiceIntegration) {
  switch (invoiceIntegration.status) {
    case invoiceIntegrationModel.Status.InvoiceSyncFailed:
      return getFailureMessage(invoiceIntegration, FailureType.Invoice);
    case invoiceIntegrationModel.Status.PaymentSyncFailed:
      return getFailureMessage(invoiceIntegration, FailureType.Payment);
    case invoiceIntegrationModel.Status.RefundSyncFailed:
      return getFailureMessage(invoiceIntegration, FailureType.Refund);
    default:
      return null;
  }
}
function getFailureMessage(integrationFailure, failureType) {
  const {
    code,
    param
  } = integrationFailureModel.customFailure(integrationFailure);
  switch (code) {
    case FailureCode.Unknown:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed. ", getContactSupportButton(true), " for assistance.");
    case FailureCode.DuplicateDocumentNumberError:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because this invoice number is already in use. Edit or remove the invoice in QuickBooks Online. To prevent future occurrences, ", getReadMoreButton(), ".");
    case FailureCode.AmountCalculationIncorrect:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed. ", getContactSupportButton(true), " for assistance.");
    case FailureCode.CustomerDeleted:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the customer assigned is inactive in QuickBooks Online. Reactivate", ' ', param, " and then click \"Retry sync.\" ", getReadMoreButton(), ".");
    case FailureCode.ItemDeleted:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the service assigned is inactive in QuickBooks Online. Reactivate", ' ', param, " and then click \"Retry sync.\" ", getReadMoreButton(), ".");
    case FailureCode.ClassDeleted:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the class assigned is inactive. Reactivate ", param, " in QuickBooks Online and then click \"Retry sync.\" ", getReadMoreButton(), ".");
    case FailureCode.InvoiceAmountNotMatching:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the amount in QuickBooks Online was changed and no longer matches the invoice amount in Anchor. Fix the invoice amount in QuickBooks Online and then click \"Retry sync.\"");
    case FailureCode.ObjectNotFound:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because an account, customer, item, or vendor you are trying to use has been made inactive. Review and reactivate in QuickBooks Online and then click \"Retry sync.\"", ' ', getReadMoreButton(), ".");
    case FailureCode.AccountDeleted:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the assigned account is inactive in QuickBooks Online. Reactivate", ' ', param, " and then click \"Retry sync.\" ", getReadMoreButton(), ".");
    case FailureCode.VendorDeleted:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the assigned vendor is inactive in QuickBooks Online. Reactivate", ' ', param, " and then click \"Retry sync.\" ", getReadMoreButton(), ".");
    case FailureCode.InvoicePaid:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because a payment for this invoice has already been created in QuickBooks Online. Remove the payment and then click \"Retry sync\".");
    case FailureCode.DepositTotalAmountNotMatching:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the payout amount does not match the deposit amount in QuickBooks Online. ", getContactSupportButton(true), " for further assistance.");
    case FailureCode.TransactionCannotBeAppliedToDeposit:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the payment ", param, " is already applied to a QuickBooks Online deposit. To resolve the issue, find the deposit in QuickBooks Online, uncheck the payment, save, and then click \"Retry sync\" in Anchor. ", getReadMoreButton(unlinkPaymentUrl), ".");
    case FailureCode.InvoiceDeleted:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the invoice could not be found in QuickBooks Online. It might be deleted or modified. ", getContactSupportButton(true), " for further assistance.");
    case FailureCode.PaymentDeleted:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the payment ", param, " could not be found in QuickBooks Online. It might be deleted or modified. ", getContactSupportButton(true), " for further assistance.");
    case FailureCode.AccountPeriodClosed:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed because the account period has closed in QuickBooks Online, preventing updates to the account books. To resolve, either turn \"Close the books\" off or change the closing date. Then, click 'Retry sync' in Anchor. ", getReadMoreButton(accountPeriodClosedUrl), ".");
    case FailureCode.InvalidCompanyStatus:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed due to an issue with the QuickBooks Online account subscription. Please update your account subscription in QuickBooks Online and then click \"Retry Sync\".");
    case FailureCode.MissingPermissions:
      return /*#__PURE__*/React.createElement("div", null, failureType, " sync failed due to missing permissions on QuickBooks Online. Please make sure your user in QuickBooks Online has the needed access rights for this action.");
  }
}